import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { StarIcon } from '@heroicons/react/24/solid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { RESULTS_CONTROL, RESULTS_CONTROL_IDLE, SPORT_IDS, filterUrl } from './consts';

// Curated favorable framings pinned to the top of the picker (#2004). These are just
// deep-links to a sport/season range — NO fabricated stat numbers (the record shown on
// the page is always the real, queried one). A marketing page can also deep-link any of
// these directly, so the framing loads as the default view.
const FEATURED: { key: string; label: string; href: (baseUrl: string) => string }[] = [
  {
    key: 'nfl-cfb-2020',
    label: 'NFL & CFB since 2020',
    href: (b) => `${filterUrl(b, 'nfl,cfb', undefined, '')}?startDate=20200701`,
  },
  {
    key: 'all-last-5',
    label: 'Last 5 seasons, all sports',
    href: (b) => filterUrl(b, 'all', 'last-5', ''),
  },
  {
    key: 'cbb-all-time',
    label: 'CBB all-time',
    href: (b) => filterUrl(b, 'cbb', 'all-time', ''),
  },
];

const SPORT_LABEL: Record<number, string> = {
  1: 'CFB',
  2: 'CBB',
  3: 'NFL',
};

/** The single numeric sportId a picker view is scoped to, or null for a multi/all view. */
function singleSportId(sportId?: string): number | null {
  if (!sportId || sportId === 'ras' || sportId === 'all' || sportId === '0') {
    return null;
  }
  if (sportId.includes(',')) {
    return null;
  }
  const named = (SPORT_IDS as Record<string, number>)[sportId];
  if (named) {
    return named;
  }
  const n = Number(sportId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * The custom season picker (#2004): a button that opens a panel with the curated
 * Featured framings pinned on top, then the selected sport's seasons — the five most
 * recent prominent, the rest in a scroll with a soft fade affordance, and All-time last.
 * Every entry is a real deep-link (filterUrl), so SSR + marketing deep-links keep working.
 */
export function SeasonPicker({
  baseUrl,
  sportId,
  seasonId,
  seasons,
  label,
}: {
  baseUrl: string;
  sportId?: string;
  seasonId?: string;
  seasons?: API.Season[];
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const sportNum = singleSportId(sportId);

  const { recent, older } = useMemo(() => {
    if (!seasons || sportNum === null) {
      return { recent: [], older: [] as API.Season[] };
    }
    const list = seasons
      .filter((s) => s.sportId === sportNum && !s.hidden && s.endDate)
      .sort((a, b) => Date.parse(b.endDate || '') - Date.parse(a.endDate || ''));
    return { recent: list.slice(0, 5), older: list.slice(5) };
  }, [seasons, sportNum]);

  const seasonLabel = sportNum !== null ? `${SPORT_LABEL[sportNum] || ''} seasons` : 'Seasons';

  const seasonHref = (s: API.Season) => filterUrl(baseUrl, sportId, s.id, s.name);
  const isCurrentSeason = (s: API.Season, idx: number) =>
    `${s.id}` === `${seasonId}` || ((!seasonId || seasonId === 'current') && idx === 0);

  return (
    // min-w-0 lets the picker shrink beside the scope toggle on a phone; the label
    // truncates rather than wrapping the row (#2142).
    <div className="relative min-w-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        // Same RESULTS_CONTROL sizing as the sport chips so the filter row reads as one
        // set of controls rather than three unrelated components (#2004).
        className={twMerge(
          RESULTS_CONTROL,
          RESULTS_CONTROL_IDLE,
          'max-w-full shrink gap-1.5 text-zinc-900 dark:text-white',
        )}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span className="min-w-0 truncate" title={label}>
          {label}
        </span>
        <ChevronDownIcon className="size-4 shrink-0 text-zinc-400" />
      </button>

      {open && (
        <div className="absolute top-[calc(100%+6px)] left-0 z-40 w-[320px] max-w-[88vw] rounded-xl border border-zinc-950/15 bg-white p-2 shadow-xl dark:border-white/15 dark:bg-zinc-900">
          <div className="px-2 pt-1.5 pb-1 font-semibold text-[10px] text-zinc-400 uppercase tracking-wider dark:text-zinc-500">
            Featured
          </div>
          <div className="flex flex-col">
            {FEATURED.map((f) => (
              <a
                key={f.key}
                href={f.href(baseUrl)}
                className="flex items-center gap-2 rounded-lg px-2 py-2 font-semibold text-sm text-zinc-800 hover:bg-zinc-100 dark:text-zinc-100 dark:hover:bg-white/5"
              >
                <StarIcon className="size-3.5 shrink-0 text-emerald-500" />
                {f.label}
              </a>
            ))}
          </div>

          {sportNum !== null && (recent.length > 0 || older.length > 0) && (
            <>
              <div className="mt-1 border-zinc-950/10 border-t px-2 pt-2 pb-1 font-semibold text-[10px] text-zinc-400 uppercase tracking-wider dark:border-white/10 dark:text-zinc-500">
                {seasonLabel}
              </div>
              {/* Recent seasons prominent; older ones in a capped scroll with a soft
                  bottom fade so it's clear there's more below. */}
              <div className="relative">
                <div className="flex max-h-[190px] flex-col overflow-y-auto pb-2 [scrollbar-width:thin]">
                  {recent.map((s, i) => (
                    <a
                      key={s.id}
                      href={seasonHref(s)}
                      className={twMerge(
                        'rounded-lg px-2 py-1.5 text-sm text-zinc-800 hover:bg-zinc-100 dark:text-zinc-100 dark:hover:bg-white/5',
                        isCurrentSeason(s, i) &&
                          'bg-zinc-100 font-semibold ring-1 ring-zinc-950/10 dark:bg-white/5 dark:ring-white/10',
                      )}
                    >
                      {s.name}
                    </a>
                  ))}
                  {older.length > 0 && (
                    <div className="px-2 pt-2 pb-1 font-semibold text-[10px] text-zinc-400 uppercase tracking-wider dark:text-zinc-500">
                      Older seasons
                    </div>
                  )}
                  {older.map((s) => (
                    <a
                      key={s.id}
                      href={seasonHref(s)}
                      className={twMerge(
                        'rounded-lg px-2 py-1.5 text-sm text-zinc-800 hover:bg-zinc-100 dark:text-zinc-100 dark:hover:bg-white/5',
                        `${s.id}` === `${seasonId}` &&
                          'bg-zinc-100 font-semibold ring-1 ring-zinc-950/10 dark:bg-white/5 dark:ring-white/10',
                      )}
                    >
                      {s.name}
                    </a>
                  ))}
                  <a
                    href={filterUrl(baseUrl, sportId, 'all-time', '')}
                    className={twMerge(
                      'rounded-lg px-2 py-1.5 text-sm text-zinc-800 hover:bg-zinc-100 dark:text-zinc-100 dark:hover:bg-white/5',
                      seasonId === 'all-time' &&
                        'bg-zinc-100 font-semibold ring-1 ring-zinc-950/10 dark:bg-white/5 dark:ring-white/10',
                    )}
                  >
                    All-time
                  </a>
                </div>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-5 bg-gradient-to-t from-white to-transparent dark:from-zinc-900" />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
